"use client";

import { useState, useCallback, type ReactNode } from "react";
import { AnalysisProvider, useAnalysis } from "@/contexts/analysis-context";
import { useAuth } from "@/hooks/use-auth";
import { LoginModal } from "@/components/login-modal";
import { ThemeProvider } from "@/providers/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AnalyticsTracker } from "@/components/analytics-tracker";
import { ConsentProvider } from "@/contexts/consent-context";
import { ConsentBanner } from "@/components/consent/consent-banner";

/**
 * 로그인 인터셉트 레이어
 * AnalysisContext에서 __LOGIN_REQUIRED__ 에러 발생 시 모달 표시
 */
function LoginInterceptLayer({ children }: { children: ReactNode }) {
  const { state } = useAnalysis();
  const { needsLogin } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  // 분석 시작 시 401 → __LOGIN_REQUIRED__ 에러가 발생하면 모달 표시
  const loginRequired = state.error === "__LOGIN_REQUIRED__" || (needsLogin && showLogin);

  return (
    <>
      {children}
      <LoginModal
        open={loginRequired || showLogin}
        onOpenChange={setShowLogin}
      />
    </>
  );
}

/**
 * 앱 전체 Provider 래퍼
 * layout.tsx에서 한 번 감싸면 모든 하위 컴포넌트에서 사용 가능
 *
 * Provider 순서:
 * 1. ThemeProvider — 다크/라이트 테마 전환 (data-theme 속성)
 * 2. TooltipProvider — shadcn/ui Tooltip 전역 설정
 * 3. ConsentProvider — 동의 상태 (mypage, 어드민 등 하위 컴포넌트가 hook 사용 가능)
 * 4. AnalysisProvider — SSE 기반 분석 상태 관리
 * 5. LoginInterceptLayer — 401 에러 시 로그인 모달
 */
export function AppProviders({ children, biteSurface = false }: { children: ReactNode; biteSurface?: boolean }) {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <ConsentProvider advertisingAvailable={biteSurface}>
          <AnalysisProvider>
            <LoginInterceptLayer>{children}</LoginInterceptLayer>
          </AnalysisProvider>
          {/* 사용자 결정 #18: 행동 추적 (모든 페이지) */}
          <AnalyticsTracker />
          {/* Phase 0 (온톨로지 V0): 동의 배너 — 첫 방문 시 표시 */}
          <ConsentBanner brandName={biteSurface ? "Bite" : "불스리서치"} biteSurface={biteSurface} />
        </ConsentProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

/** 로그인 모달을 수동으로 트리거할 수 있는 훅 */
export function useLoginModal() {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);
  return { open, show, hide, setOpen };
}
